/** Google Tag Manager + Consent Mode v2 helpers. */

import { applyClarityForConsent } from "./clarity";

export const COOKIE_CONSENT_KEY = "cookie_consent";
export const COOKIE_CONSENT_PENDING_KEY = "cookie_consent_pending";

export type CookieConsentValue = "granted" | "denied";

type DataLayerObject = Record<string, unknown> | IArguments;

declare global {
  interface Window {
    dataLayer: DataLayerObject[];
    gtag?: (...args: unknown[]) => void;
  }
}

const DENIED_CONSENT = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
} as const;

const GRANTED_ANALYTICS = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "granted",
} as const;

let gtmInitialized = false;

function ensureDataLayer(): DataLayerObject[] {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

/** Official gtag stub: must push `arguments`, not a plain array. */
function gtag(this: void, ..._args: unknown[]) {
  // eslint-disable-next-line prefer-rest-params
  ensureDataLayer().push(arguments);
}

function ensureGtag() {
  if (typeof window.gtag !== "function") {
    window.gtag = gtag;
  }
}

export function getStoredConsent(): CookieConsentValue | null {
  const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (raw === "granted" || raw === "denied") return raw;
  return null;
}

export function setStoredConsent(value: CookieConsentValue): void {
  localStorage.setItem(COOKIE_CONSENT_KEY, value);
}

export function setCookieConsentPending(): void {
  localStorage.setItem(COOKIE_CONSENT_PENDING_KEY, "1");
}

export function isCookieConsentPending(): boolean {
  return localStorage.getItem(COOKIE_CONSENT_PENDING_KEY) === "1";
}

export function clearCookieConsentPending(): void {
  localStorage.removeItem(COOKIE_CONSENT_PENDING_KEY);
}

/** Push Consent Mode defaults (analytics on until rejected). Call before loading GTM. */
export function setConsentDefaults(): void {
  ensureGtag();
  window.gtag!("consent", "default", {
    ...GRANTED_ANALYTICS,
    wait_for_update: 500,
  });
}

function applyConsentState(granted: boolean): void {
  ensureGtag();
  window.gtag!("consent", "update", granted ? GRANTED_ANALYTICS : DENIED_CONSENT);
  applyClarityForConsent(granted);
}

/**
 * Apply stored choice, or default to tracking enabled (opt-out).
 * Does not write localStorage when there is no stored choice yet.
 */
export function applyStoredConsent(): CookieConsentValue | null {
  const stored = getStoredConsent();
  applyConsentState(stored !== "denied");
  return stored;
}

export function updateConsent(granted: boolean): void {
  applyConsentState(granted);
  setStoredConsent(granted ? "granted" : "denied");
  clearCookieConsentPending();
}

function gtmScriptAlreadyPresent(): boolean {
  return Boolean(
    document.querySelector('script[src*="googletagmanager.com/gtm.js"]'),
  );
}

/**
 * Load the GTM container script. Consent Mode should already be set
 * (inline snippet in index.html, or setConsentDefaults / applyStoredConsent).
 */
export function initGtm(containerId: string | undefined): void {
  const id = containerId?.trim();
  if (gtmInitialized || gtmScriptAlreadyPresent()) {
    gtmInitialized = true;
    return;
  }
  if (!id) {
    if (import.meta.env.DEV) {
      console.info(
        "[analytics] VITE_GTM_ID is not set — GTM will not load. Set it in .env and restart Vite / rebuild for production.",
      );
    }
    return;
  }
  if (typeof document === "undefined") return;

  ensureDataLayer();
  ensureGtag();
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  gtmInitialized = true;
}

export function pushEvent(event: string, params: Record<string, unknown> = {}): void {
  ensureDataLayer();
  window.dataLayer.push({ event, ...params });
}

/** SPA route change — Custom Event `app_page_view` (not gtag / History Change). */
export function trackSpaPageView(path: string, title: string): void {
  pushEvent("app_page_view", {
    page_path: path,
    page_title: title,
    page_location: typeof window !== "undefined" ? window.location.href : undefined,
  });
}

export function setUser(userId: number | null): void {
  ensureDataLayer();
  if (userId == null) {
    window.dataLayer.push({
      event: "user_clear",
      user_id: null,
      logged_in: false,
    });
    return;
  }
  window.dataLayer.push({
    event: "user_set",
    user_id: String(userId),
    logged_in: true,
  });
}
